import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Tracks actual PWA installs (not just "Install App" button clicks).
 *
 * - On Android/Chrome, fires when the `appinstalled` event is dispatched.
 * - On iOS Safari, the OS provides no install event. Instead, the first time
 *   the app boots in standalone display-mode (i.e. launched from the home
 *   screen icon after Add to Home Screen) we fire a one-time beacon.
 *
 * IMPORTANT: the beacon must not fire before the Supabase session has been
 * restored from storage, otherwise the edge function sees no auth header and
 * the install is recorded as anonymous. We therefore wait for auth to resolve
 * (with a timeout) before sending.
 */
const STORAGE_KEY = "r0hub_app_install_logged_v1";

function detectPlatformSlug(): string {
  if (typeof navigator === "undefined") return "app-installed-other";
  const ua = navigator.userAgent || "";
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const android = /Android/i.test(ua);
  if (iOS) return "app-installed-ios";
  if (android) return "app-installed-android";
  return "app-installed-other";
}

/** Resolves once a session is known, or after `timeoutMs` if signed out. */
async function waitForSession(timeoutMs = 8000): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  if (data.session) return true;

  return new Promise<boolean>((resolve) => {
    let settled = false;
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session || settled) return;
      settled = true;
      clearTimeout(timer);
      listener.subscription.unsubscribe();
      resolve(true);
    });
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      listener.subscription.unsubscribe();
      resolve(false);
    }, timeoutMs);
  });
}

async function sendInstallBeacon(slug: string) {
  try {
    if (localStorage.getItem(STORAGE_KEY)) return;
  } catch {
    // localStorage unavailable - still try once per page load
  }

  // Wait for the restored session so the install is attributed to the user.
  await waitForSession();

  try {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
  } catch {
    /* noop */
  }

  try {
    const { error } = await supabase.functions.invoke("track-content-view", {
      body: { content_type: "link_click", content_slug: slug },
    });
    if (error) throw error;
  } catch {
    // best-effort; clear flag so we can retry next launch if it failed
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
  }
}

export function useAppInstallTracker() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Detect "already installed and now launched from home screen"
    const standaloneMql = window.matchMedia?.("(display-mode: standalone)").matches;
    const iosStandalone = (window.navigator as any).standalone === true;
    if (standaloneMql || iosStandalone) {
      void sendInstallBeacon(detectPlatformSlug());
    }

    // Android/Chrome real-time install event
    const onInstalled = () => void sendInstallBeacon(detectPlatformSlug());
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);
}
